import React, { useState, useCallback, useEffect } from 'react';
import { documentAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Upload, File, X, Spinner, FilePdf, Warning } from '@phosphor-icons/react';

const categories = [
  { value: 'expense', label: 'Expense Receipt' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'tax', label: 'Tax Document' },
  { value: 'receipt', label: 'General Receipt' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' }
];

const UploadDocumentModal = ({ open, onClose, onSuccess, companyId }) => {
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [usage, setUsage] = useState(null);
  const [formData, setFormData] = useState({
    category: 'expense',
    description: ''
  });

  useEffect(() => {
    if (open) {
      fetchUsage();
    }
  }, [open]);

  const fetchUsage = async () => {
    try {
      const response = await documentAPI.getUsage();
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const validFiles = [];
    const newPreviews = [...previews];

    Array.from(files).forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported format. Use JPEG, PNG, WEBP, or PDF`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return;
      }
      validFiles.push(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews(prev => [...prev, { name: file.name, url: reader.result, type: 'image' }]);
        };
        reader.readAsDataURL(file);
      } else {
        newPreviews.push({ name: file.name, type: 'pdf' });
        setPreviews(newPreviews);
      }
    });

    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [selectedFiles, previews]);

  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) {
      processFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setPreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    // Check limit
    if (usage && selectedFiles.length > usage.remaining) {
      toast.error(`You can only upload ${usage.remaining} more documents. Please upgrade your plan.`);
      return;
    }

    setLoading(true);

    try {
      if (selectedFiles.length === 1) {
        // Single file upload with AI extraction
        const file = selectedFiles[0];
        const base64 = await fileToBase64(file);
        
        toast.info('Uploading and analyzing document...');
        
        await documentAPI.upload(companyId, {
          filename: file.name,
          content_type: file.type,
          base64_data: base64,
          category: formData.category,
          description: formData.description || null
        });
        
        toast.success('Document uploaded and analyzed!');
      } else {
        // Batch upload
        toast.info(`Uploading ${selectedFiles.length} documents...`);
        
        const documents = await Promise.all(
          selectedFiles.map(async (file) => ({
            filename: file.name,
            content_type: file.type,
            base64_data: await fileToBase64(file),
            category: formData.category,
            description: formData.description || null
          }))
        );

        const result = await documentAPI.batchUpload(companyId, documents);
        toast.success(`${result.data.uploaded} documents uploaded!`);
        
        if (result.data.failed > 0) {
          toast.warning(`${result.data.failed} documents failed to upload`);
        }
      }

      // Reset form
      setSelectedFiles([]);
      setPreviews([]);
      setFormData({ category: 'expense', description: '' });
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const usagePercent = usage ? (usage.current_count / usage.limit) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1A3C34]">Upload Documents</DialogTitle>
          <DialogDescription className="text-[#1A3C34]/60">
            Upload receipts, invoices, or tax documents - AI will automatically extract the data
          </DialogDescription>
        </DialogHeader>

        {/* Usage indicator */}
        {usage && (
          <div className="p-3 bg-[#F4F4F0] rounded-lg">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1A3C34]/70">Document Storage</span>
              <span className="text-[#1A3C34] font-medium">
                {usage.current_count} / {usage.limit === 9999 ? '∞' : usage.limit}
              </span>
            </div>
            <Progress value={Math.min(usagePercent, 100)} className="h-2" />
            {usage.remaining <= 5 && usage.remaining > 0 && (
              <p className="text-xs text-[#D4A373] mt-1 flex items-center gap-1">
                <Warning className="w-3 h-3" />
                Only {usage.remaining} uploads remaining
              </p>
            )}
            {usage.remaining === 0 && (
              <p className="text-xs text-[#E76F51] mt-1 flex items-center gap-1">
                <Warning className="w-3 h-3" />
                Limit reached. Upgrade to upload more.
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              dragActive 
                ? 'border-[#1A3C34] bg-[#1A3C34]/5' 
                : 'border-[#1A3C34]/20 hover:border-[#1A3C34]/40'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 justify-center max-h-32 overflow-y-auto">
                  {previews.map((preview, index) => (
                    <div key={index} className="relative group">
                      {preview.type === 'image' ? (
                        <img 
                          src={preview.url} 
                          alt={preview.name}
                          className="h-16 w-16 object-cover rounded-lg border"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-[#E76F51]/10 rounded-lg border flex items-center justify-center">
                          <FilePdf className="w-6 h-6 text-[#E76F51]" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#2A9D8F]">
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                </p>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFiles}
                  className="text-[#1A3C34]/60"
                >
                  Clear all
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-[#1A3C34]/40 mx-auto mb-2" />
                <p className="text-[#1A3C34] font-medium mb-1">
                  Drag and drop files here
                </p>
                <p className="text-sm text-[#1A3C34]/60 mb-2">
                  or click to browse
                </p>
                <p className="text-xs text-[#1A3C34]/40">
                  JPEG, PNG, WEBP, PDF (max 10MB each)
                </p>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileSelect}
              multiple
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="file-input"
            />
          </div>

          {/* Category Select */}
          <div className="space-y-2">
            <Label className="text-[#1A3C34]">Document Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger className="border-[#1A3C34]/20" data-testid="category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-[#1A3C34]">Notes (Optional)</Label>
            <Textarea
              placeholder="Add any notes about these documents..."
              className="border-[#1A3C34]/20 focus:border-[#1A3C34] min-h-[60px] resize-none"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              data-testid="description-input"
            />
          </div>

          {/* AI Info */}
          <div className="p-3 bg-[#2A9D8F]/10 rounded-lg border border-[#2A9D8F]/20">
            <p className="text-sm text-[#1A3C34]/80">
              <strong>AI Analysis:</strong> Our AI will automatically extract vendor names, amounts, dates, and categorize expenses from your documents.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-[#1A3C34]/20 text-[#1A3C34]"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
              disabled={loading || selectedFiles.length === 0 || (usage && usage.remaining === 0)}
              data-testid="upload-btn"
            >
              {loading ? (
                <>
                  <Spinner className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Analyze
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentModal;
